import { Response } from "miragejs";
import { registerRoute } from "../server";

const MIRAGE_SERVICE_REQUEST_ID = "MIRAGE_CLAIMS_SR";
const ON_SUBMISSION_RRD_ID = "MIRAGE_RRD_ON_SUBMISSION";
const POST_SUBMISSION_RRD_ID = "MIRAGE_RRD_POST_SUBMISSION";
const CLAIMS_CONDITION_ID = "mirage-claims-condition";
const KEYPHRASE_CONDITION_ID = "mirage-keyphrase-condition";
const STANDARD_CONCEPT_CONDITION_ID = "mirage-standard-concept-condition";

const claimsEvidenceItems = [
  {
    serviceDate: "01/15/2026",
    serviceCategoryName: "Musculoskeletal",
    procedureCode: ["97110", "97112", "97530"],
    procedureCodeDescription: [
      "Therapeutic exercises to develop strength and endurance, range of motion and flexibility",
      "Neuromuscular reeducation of movement, balance, coordination, kinesthetic sense, posture, and proprioception",
      "Therapeutic activities, direct patient contact",
    ],
    diagnosisCode: ["M25.561", "S82.851A"],
    diagnosisCodeDescription: [
      "Pain in right knee",
      "Displaced trimalleolar fracture of right lower leg, initial encounter for closed fracture",
    ],
  },
  {
    serviceDate: "02/03/2026",
    serviceCategoryName: "Musculoskeletal",
    procedureCode: ["29881", "99999"],
    procedureCodeDescription: [
      "Arthroscopy, knee, surgical; with meniscectomy",
      "This claim contains a procedure code that may be invalid.",
    ],
    diagnosisCode: ["M17.11", "ZZZ99"],
    diagnosisCodeDescription: [
      "Unilateral primary osteoarthritis, right knee",
      "This claim contains a diagnosis code that may be invalid.",
    ],
  },
];

const ruleLabel = {
  id: "60118eebbfae1300019ef3b7",
  name: "Request management Mirage",
};

const buildRuleRunDescription = (
  id: string,
  ruleRunTiming: "ON_SUBMISSION" | "POST_SUBMISSION"
) => ({
  id,
  dateCreated: "2026-05-07T16:00:00Z",
  lastUpdated:
    ruleRunTiming === "ON_SUBMISSION"
      ? "2026-05-07T16:05:00Z"
      : "2026-05-07T16:15:00Z",
  ruleRunTiming,
  serviceRequest: {
    id: MIRAGE_SERVICE_REQUEST_ID,
  },
  dataModel: {
    patientId: "MIRAGE_PATIENT",
    patientGender: "F",
    patientAge: 47,
    authType: "Prior authorization",
    placeOfServiceName: ["Office"],
    clinicalService: {
      id: "MIRAGE_CLINICAL_SERVICE",
    },
    serviceRequestProcedureCodes: ["29881"],
    serviceRequestDiagnosisCodes: ["M25.561", "S82.851A"],
    attachments: 2,
  },
  results: [
    {
      rule: {
        id: "mirage-claims-rule",
      },
      outcome: true,
      skipped: false,
      evaluationFailed: false,
      ruleBody: {
        id: "mirage-claims-rule",
        title: "Mirage claims drawer rule",
        ruleLabel,
        ruleLabelPriorityPublished: 0,
        condition: {
          conditionId: CLAIMS_CONDITION_ID,
          dndId: CLAIMS_CONDITION_ID,
          evidenceData: JSON.stringify(claimsEvidenceItems),
          evaluator: {
            type: "FindingStore",
            outcome: true,
            findingType: {
              name: "ClinicalEncounterV2",
            },
            findingRecordFilters: [],
            aggregator: {
              type: "count",
              comparator: "greater_than_equal",
              targetValue: {
                value: 1,
              },
            },
          },
        },
      },
    },
    {
      rule: {
        id: "mirage-keyphrase-rule",
      },
      outcome: true,
      skipped: false,
      evaluationFailed: false,
      ruleBody: {
        id: "mirage-keyphrase-rule",
        title: "Mirage keyphrase evidence rule",
        ruleLabel,
        ruleLabelPriorityPublished: 0,
        condition: {
          conditionId: KEYPHRASE_CONDITION_ID,
          dndId: KEYPHRASE_CONDITION_ID,
          evaluator: {
            type: "KeyphraseSearch",
            outcome: true,
            operator: "contains_any",
            assertionType: "positive",
            phrases: ["knee", "pain"],
          },
        },
      },
    },
    {
      rule: {
        id: "mirage-standard-concept-rule",
      },
      outcome: true,
      skipped: false,
      evaluationFailed: false,
      ruleBody: {
        id: "mirage-standard-concept-rule",
        title: "Mirage standard concept evidence rule",
        ruleLabel,
        ruleLabelPriorityPublished: 0,
        condition: {
          conditionId: STANDARD_CONCEPT_CONDITION_ID,
          dndId: STANDARD_CONCEPT_CONDITION_ID,
          evaluator: {
            type: "StandardConcept",
            outcome: true,
            operator: "contains_any",
            concepts: [
              {
                conceptId: "123975002",
                conceptName: "Trimalleolar fracture",
                minimumCount: 1,
              },
            ],
          },
        },
      },
    },
  ],
  ruleSetStatusInfo: {
    ruleId: "mirage-claims-rule",
    ruleTitle: "Mirage claims drawer rule",
    authStatusToSetTo: "APPROVED",
  },
});

const ruleRunDescriptions = [
  buildRuleRunDescription(ON_SUBMISSION_RRD_ID, "ON_SUBMISSION"),
  buildRuleRunDescription(POST_SUBMISSION_RRD_ID, "POST_SUBMISSION"),
];

const evidenceByConditionId = {
  [CLAIMS_CONDITION_ID]: {
    serviceRequestId: MIRAGE_SERVICE_REQUEST_ID,
    conditionId: CLAIMS_CONDITION_ID,
    kind: "claims",
    supported: true,
    emptyStateText: "No claim evidence is available for this condition.",
    groups: [
      {
        title: "Claims",
        items: claimsEvidenceItems,
      },
    ],
  },
  [KEYPHRASE_CONDITION_ID]: {
    serviceRequestId: MIRAGE_SERVICE_REQUEST_ID,
    conditionId: KEYPHRASE_CONDITION_ID,
    kind: "keyphrase",
    supported: true,
    emptyStateText: "No keyphrase evidence matched this condition.",
    groups: [
      {
        title: "knee, pain",
        subtitle: "positive",
        items: [
          {
            label: "medical_service_request.pdf",
            attachmentId: "MIRAGE_ATTACHMENT_1",
            attachmentName: "medical_service_request.pdf",
            pageNumber: 2,
            matchedText: "knee pain",
            text: "knee pain",
            contextBefore: "The patient reports persistent",
            contextAfter: "after conservative therapy.",
          },
        ],
      },
    ],
  },
  [STANDARD_CONCEPT_CONDITION_ID]: {
    serviceRequestId: MIRAGE_SERVICE_REQUEST_ID,
    conditionId: STANDARD_CONCEPT_CONDITION_ID,
    kind: "standardConcept",
    supported: true,
    emptyStateText: "No standard concept evidence matched this condition.",
    groups: [
      {
        title: "Trimalleolar fracture",
        subtitle: "123975002",
        items: [
          {
            label: "medical_service_request.pdf",
            attachmentId: "MIRAGE_ATTACHMENT_2",
            attachmentName: "medical_service_request.pdf",
            conceptId: "123975002",
            conceptName: "Trimalleolar fracture",
            count: 1,
            matchedText: "trimalleolar fracture",
            text: "trimalleolar fracture",
            pageNumber: 2,
            spans: [
              {
                attachmentId: "MIRAGE_ATTACHMENT_2",
                attachmentName: "medical_service_request.pdf",
                pageNumber: 2,
                matchedText: "trimalleolar fracture",
                text: "trimalleolar fracture",
                contextBefore: "Imaging demonstrates a",
                contextAfter: "of the right ankle.",
                startPage: 2,
                startBlock: 1,
                startParagraph: 3,
                startLine: 4,
                startWord: 6,
                endPage: 2,
                endBlock: 1,
                endParagraph: 3,
                endLine: 4,
                endWord: 7,
                left: 0.24,
                top: 0.38,
                width: 0.18,
                height: 0.02,
              },
            ],
          },
        ],
      },
    ],
  },
};

export default function registerRoutes() {
  registerRoute({
    id: "serviceRequestRuleRunDescriptionSearch",
    route: "serviceRequestRuleRunDescription",
    method: "get",
    routeConfig: {
      slow: true,
      slowMs: 800,
    },
    generateResponse: function (config, schema, request) {
      const serviceRequestQuery = request.queryParams.serviceRequest;
      const serviceRequestId = serviceRequestQuery?.startsWith("eq:")
        ? serviceRequestQuery.replace("eq:", "")
        : serviceRequestQuery;

      if (serviceRequestId && serviceRequestId !== MIRAGE_SERVICE_REQUEST_ID) {
        return [];
      }

      if (request.queryParams.minimalExpansion === "true") {
        return ruleRunDescriptions.map(
          ({
            id,
            dateCreated,
            lastUpdated,
            ruleRunTiming,
            serviceRequest,
          }) => ({
            id,
            dateCreated,
            lastUpdated,
            ruleRunTiming,
            serviceRequest,
          })
        );
      }

      return ruleRunDescriptions;
    },
  });

  registerRoute({
    id: "serviceRequestRuleRunDescriptionGet",
    route: "serviceRequestRuleRunDescription/:id",
    method: "get",
    routeConfig: {
      slow: true,
      slowMs: 600,
    },
    generateResponse: function (config, schema, request) {
      const ruleRunDescription = ruleRunDescriptions.find(
        ({ id }) => id === request.params.id
      );

      if (!ruleRunDescription) {
        return new Response(
          404,
          {},
          { message: "Rule run description not found" }
        );
      }

      return ruleRunDescription;
    },
  });

  registerRoute({
    id: "serviceRequestRuleRunDescriptionEvidenceGet",
    route: "serviceRequestRuleRunDescription/:rrdId/evidence/:conditionId",
    method: "get",
    routeConfig: {
      slow: true,
      slowMs: 700,
    },
    generateResponse: function (config, schema, request) {
      const ruleRunDescription = ruleRunDescriptions.find(
        ({ id }) => id === request.params.rrdId
      );
      const evidence =
        evidenceByConditionId[
          request.params.conditionId as keyof typeof evidenceByConditionId
        ];

      if (!ruleRunDescription || !evidence) {
        return new Response(
          404,
          {},
          { message: "Condition evidence not found" }
        );
      }

      return evidence;
    },
  });
}
